import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

import {
  UserEventRsvp,
  useCurrentUserEventRsvp,
  useEventInterestDataQuery,
} from '@queries/EventQueries';

import { useRsvpToEventMutation } from '@mutations/EventMutations';

import { useAnalytics } from '@hooks/useAnalytics';

import { useAuthGate } from '../../../LogInScreen/AuthGateContext';

export const useEventRsvpActions = (
  {
    eventId,
    eventTitle,
    actionToken,
  }: { eventId: string; eventTitle?: string; actionToken?: string },
  referredBy?: string,
) => {
  const { track } = useAnalytics();
  const queryClient = useQueryClient();
  const { rsvp: queryRsvp } = useCurrentUserEventRsvp(eventId);
  const { rsvpToEventAsync, isSubmitting } = useRsvpToEventMutation();
  const invalidateEventInterestDataQuery = () =>
    queryClient.invalidateQueries({ queryKey: useEventInterestDataQuery.queryKey(eventId) });
  const [updatedRsvp, setUpdatedRsvp] = useState<
    Partial<Pick<UserEventRsvp, 'rsvp' | 'eventInterest'>>
  >({});

  const { createAuthGateHandler } = useAuthGate();

  const rsvp = useMemo(() => {
    return {
      ...queryRsvp,
      ...updatedRsvp,
    };
  }, [queryRsvp, updatedRsvp]);

  const authOptions = {
    authTitle: `RSVP to ${eventTitle ?? 'this event'}`,
    authMessage: `Sign in to add this to your calendar, see who's going, and get reminders before it starts.`,
  };

  const handleMarkAttending = () => {
    track('Click Event RSVP', { eventId, text: 'Attending' });

    createAuthGateHandler(async () => {
      const updatedRsvpProperties: Partial<Pick<UserEventRsvp, 'rsvp' | 'eventInterest'>> = {
        rsvp: rsvp?.rsvp === 'yes' ? null : 'yes',
      };
      track('Update RSVP', { eventId, updatedRsvpProperties });

      setUpdatedRsvp((prev) => ({ ...prev, ...updatedRsvpProperties }));

      await rsvpToEventAsync({
        eventId,
        referredBy,
        actionToken,
        ...updatedRsvpProperties,
      });

      invalidateEventInterestDataQuery();
    }, authOptions);
  };

  const handleMarkInterested = () => {
    track('Click Event RSVP', { eventId, text: 'Interested' });

    createAuthGateHandler(async () => {
      const isCurrentlyInterested = rsvp?.rsvp !== 'yes' && rsvp?.eventInterest === 'interested';

      const updatedRsvpProperties: Partial<Pick<UserEventRsvp, 'rsvp' | 'eventInterest'>> = {
        rsvp: isCurrentlyInterested ? undefined : null,
        eventInterest: isCurrentlyInterested ? null : 'interested',
      };

      track('Update RSVP', { eventId, updatedRsvpProperties });

      setUpdatedRsvp((prev) => ({ ...prev, ...updatedRsvpProperties }));
      await rsvpToEventAsync({
        eventId,
        referredBy,
        actionToken,
        ...updatedRsvpProperties,
      });
      invalidateEventInterestDataQuery();
    }, authOptions);
  };

  const isCurrentUserAttending = rsvp?.rsvp === 'yes';
  const isCurrentUserInterested = rsvp?.rsvp !== 'yes' && rsvp?.eventInterest === 'interested';

  return {
    handleMarkAttending,
    handleMarkInterested,
    rsvp,
    isCurrentUserAttending,
    isCurrentUserInterested,
    isUpdatingRsvp: isSubmitting,
  };
};
